import { ApiError } from "./errors";
export type JsonObject = Record<string, unknown>;
export interface SourceRepo {
  name: string;
  url: string;
  branch: string;
  auto_refresh: boolean;
  copy_enabled: boolean;
  phase: string;
  ready?: boolean;
  updating?: boolean;
  freshness: string;
  last_result?: string;
  head?: string;
  error?: string;
  attempt?: number;
  checked_at?: string;
  updated_at?: string;
  last_attempt_at?: string;
  remote_head?: string;
  remote_checked_at?: string;
  remote_error?: string;
  behind_count?: number | null;
  comparison_error?: string;
  progress_operation?: string;
  progress_message?: string;
  progress_events?: { at: string; operation: string; message: string }[];
  next_update?: string;
  started_at?: string;
  finished_at?: string;
  filesystem_omissions?: string[];
  attempt_errors?: { attempt: number; at: string; error: string }[];
}
export interface SourceRefreshRun {
  id: string;
  trigger: string;
  status: string;
  started_at: string;
  finished_at?: string;
  success_count: number;
  failure_count: number;
  running_count: number;
  execution_count: number;
  pending_count: number;
  skipped_count: number;
  total_count: number;
  repositories: Record<string, SourceRepo>;
  cancel_requested?: boolean;
}
export interface SourceRepoStatus {
  items: SourceRepo[];
  history: SourceRefreshRun[];
  ready: boolean;
  updating: boolean;
  auto_refresh: boolean;
  history_count: number;
  page: number;
  page_size: number;
  schedule?: {
    task_id: string;
    enabled: boolean;
    interval_seconds: number;
    start_at: string | null;
    next_run: string | null;
  };
}
export interface UserRecord extends JsonObject {
  id: number;
  username: string;
  email?: string;
  role: "administrator" | "operator" | "viewer";
  is_active: boolean;
  status?: "active" | "pending";
  created_at: string;
  last_login: string | null;
}
export interface KeyMetadata extends JsonObject {
  id: string;
  name: string;
  scopes: string[];
  prefix?: string;
  created_at?: string;
  last_used_at?: string | null;
  revoked_at?: string | null;
}
export interface TaskRecord extends JsonObject {
  config_source?: CodexConfigSource;
  id: string;
  category: "script" | "codex" | "builtin";
  description?: string;
  builtin_key?: string;
  script_path?: string;
  start_at?: string | null;
  next_run?: string | null;
  name: string;
  shell: "bash" | "cmd" | "python" | "javascript";
  script: string;
  schedule_mode?: "interval" | "cron";
  cron_expression?: string;
  timezone?: string;
  interval_seconds: number;
  timeout_seconds: number;
  enabled: boolean;
  env_keys?: string[];
  provider_base_url?: string;
  provider_api_key_configured?: boolean;
  model?: string | null;
  effort?: "low" | "medium" | "high" | "xhigh" | "max" | "ultra" | null;
  prompt?: string;
  system_prompt?: string;
  skills?: string[];
  compatibility_error?: JsonObject;
  last_status?: string | null;
}
export interface SkillRecord extends JsonObject {
  name: string;
  description: string;
  description_zh: string;
  updated_at: string;
  size: number;
  file_count: number;
  validation_status: string;
  enabled: boolean;
}
export interface SkillDetail extends SkillRecord {
  path?: string;
  files: SkillFileSummary[];
  preview: SkillTextFile;
}
export interface SkillFileSummary extends JsonObject {
  path: string;
  size: number;
  editable: boolean;
  sha256: string | null;
}
export interface SkillTextFile extends JsonObject {
  skill: string;
  path: string;
  content: string;
  sha256: string;
  size: number;
}
export type ExecutionMode = "restricted" | "unrestricted";
export function executionModeLabel(
  mode: ExecutionMode | null | undefined,
): string {
  return mode || "—";
}
export interface CodexModel {
  value: string;
  label: string;
  endpoints: string[];
  engines: string[];
  status: string;
  costMultiplier: number | null;
  efforts: string[];
}
export interface CodexModelCatalog {
  models: CodexModel[];
}
export interface CodexUsage {
  input: number | null;
  output: number | null;
  cached: number | null;
  cache_write: number | null;
  total: number | null;
  reasoning_output?: number | null;
  model_context_window?: number | null;
  source: "total" | "unavailable";
}
export type UsageField =
  "input" | "output" | "cached" | "cache_write" | "total";
export interface CodexUsageAggregate {
  run_count: number;
  success_count: number;
  error_count: number;
  tokens: Record<UsageField | "reasoning_output", number | null>;
  token_samples: Record<UsageField | "reasoning_output", number>;
  duration_ms_sum: number;
  duration_samples: number;
}
export interface CodexStatistics {
  timezone: string;
  window: { start_date: string; end_date: string; days: number };
  daily: (CodexUsageAggregate & { date: string })[];
  week: CodexUsageAggregate;
  cumulative: {
    run_count: number;
    success_count: number;
    error_count: number;
    duration_samples: number;
    average_duration_ms: number | null;
  };
  coverage: {
    backfilled_at: string | null;
    earliest_available_run_at: string | null;
    history_before_backfill_may_be_missing: boolean;
  };
}
export type CodexProtocol = "openai" | "anthropic";
export type CodexConfigSource = "custom" | "local";
export interface CodexRequest extends JsonObject {
  config_source?: CodexConfigSource;
  protocol?: CodexProtocol;
}
export interface CodexProviderAudit extends JsonObject {
  requested_effort: string;
  effective_effort: string | null;
  adjustments: { code: string; message: string }[];
}
export interface CodexAudit extends JsonObject {
  provider_adapter?: CodexProviderAudit;
}
export interface CodexResponse extends JsonObject {
  config_source?: CodexConfigSource;
  protocol?: CodexProtocol | "local";
  execution_mode: ExecutionMode;
  execution_status: string;
  reasoning_effort?: string | null;
  text: string;
  model: string | null;
  effort: "low" | "medium" | "high" | "xhigh" | "max" | "ultra" | null;
  usage: JsonObject | null;
  normalized_usage?: CodexUsage;
  request_id: string;
  latency_ms: number;
  workspace: string;
  audit?: CodexAudit;
}
export interface CodexRunEvent {
  sequence: number;
  at: string;
  event: string;
  text: string;
  truncated?: boolean;
}
export interface CodexRunEvents {
  items: CodexRunEvent[];
  count: number;
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  storage: "persistent" | "legacy_snapshot";
  missing_events: number;
  event_count: number;
  max_text_chars: number;
}
export interface CodexRun extends JsonObject {
  config_source?: CodexConfigSource;
  protocol?: CodexProtocol | "local";
  progress?: {
    latest?: string;
    updated_at?: string;
    event_count?: number;
    omitted_events?: number;
    events?: {
      sequence: number;
      at: string;
      event: string;
      text: string;
      truncated: boolean;
    }[];
  };
  execution_mode?: ExecutionMode | null;
  reasoning_effort?: string;
  id: string;
  status: string;
  model: string;
  effort: string;
  created_at: string;
  completed_at?: string | null;
  latency_ms?: number | null;
  skills?: string[];
  env_keys?: string[];
  workspace?: string;
  prompt?: string;
  prompt_preview?: string;
  system_prompt?: string;
  key_hash_length?: number | null;
  output?: string | null;
  text?: string | null;
  usage?: JsonObject | null;
  normalized_usage?: CodexUsage;
  error?: string | JsonObject | null;
  audit?: CodexAudit | null;
  is_knowledge_update?: boolean;
  stop_requested?: boolean;
  stopping?: boolean;
}
export interface KnowledgeUpdateStop {
  request_id: string;
  status: string;
  stop_requested: boolean;
  stopping: boolean;
  error_code: string;
}
export interface KnowledgeUpdateRepository {
  id: string;
  label: string;
  ready?: boolean;
}
export interface KnowledgeUpdateRequest extends CodexRequest {
  repositories: string[];
  apikey?: string;
  base_url?: string;
  model?: string;
  custom_model?: string;
  reasoning_effort?: string;
  timeout_seconds: number;
}
export interface KnowledgeUpdateAccepted {
  accepted: true;
  request_id: string;
  status: "queued";
  repositories: string[];
  poll_url: string;
}
export interface KnowledgeUpdateAudit extends JsonObject {
  repositories: {
    source_name: string;
    repository_key: string;
    namespace_key: string;
    path: string;
    head: string;
  }[];
  source_versions: { source_name: string; head: string }[];
  validation_summary: JsonObject | null;
  coverage_state?: "complete" | "partial";
}
export interface CodexRunPage extends JsonObject {
  items: CodexRun[];
  count: number;
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}
export interface AuditEvent extends JsonObject {
  id: number;
  request_id: string;
  timestamp: string;
  method: string;
  path: string;
  route_name: string;
  status_code: number;
  duration_ms: number;
  operation: string;
  resource: string;
  actor_type: string;
  user_id: number | null;
  user_name: string;
  api_key_id: string;
  api_key_prefix: string;
  client_ip: string | null;
  user_agent: string;
  error_code: string;
  request_fields: string[];
  request_bytes: number;
  response_bytes: number;
  redaction: JsonObject;
}
export interface KnowledgePage<T extends JsonObject = JsonObject> {
  items: T[];
  count: number;
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface RequestCapacity {
  ok: boolean;
  long_request_limit: number;
  max_queued_requests: number;
  minimum: number;
  maximum: number;
  queue_minimum: number;
  queue_maximum: number;
  active_requests: number;
  queued_requests: number;
}

export interface MarkdownDocument extends JsonObject {
  id: string;
  file_id: string;
  namespace: string;
  relative_path: string;
  content?: string;
  converted_at?: string;
  content_sha256?: string;
}

export interface KnowledgeCatalog extends JsonObject {
  counts: Record<string, number>;
  search: { types: string[]; match: string[] };
  facets: Record<string, string[]>;
  resources: JsonObject;
}

const queryString = (query: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  return params.toString();
};

/** Read the current cookie for every request, including after login rotates it. */
export function csrfHeaders(
  path: string,
  method = "GET",
  initial?: HeadersInit,
): Headers {
  const headers = new Headers(initial);
  // Never reuse a token captured before cookie rotation or on another origin.
  headers.delete("X-CSRFToken");
  if (["GET", "HEAD", "OPTIONS", "TRACE"].includes(method.toUpperCase()))
    return headers;
  if (new URL(path, window.location.href).origin !== window.location.origin)
    return headers;
  const cookie = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith("csrftoken="));
  if (cookie)
    headers.set(
      "X-CSRFToken",
      decodeURIComponent(cookie.slice("csrftoken=".length)),
    );
  return headers;
}

let csrfRefresh: Promise<void> | null = null;
function refreshCsrfCookie(): Promise<void> {
  csrfRefresh ||= fetch("/api/auth/me", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  })
    .then(() => undefined)
    .finally(() => {
      csrfRefresh = null;
    });
  return csrfRefresh;
}

/** Retry only an explicit pre-execution CSRF rejection, never an execution error. */
export async function csrfFetch(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const method = options.method || "GET";
  const initial = new Headers(options.headers);
  const cookieMutation =
    !["GET", "HEAD", "OPTIONS", "TRACE"].includes(method.toUpperCase()) &&
    new URL(path, window.location.href).origin === window.location.origin &&
    options.credentials !== "omit";
  if (cookieMutation && !csrfHeaders(path, method).has("X-CSRFToken"))
    await refreshCsrfCookie();
  const send = () =>
    fetch(path, {
      credentials: "same-origin",
      ...options,
      headers: csrfHeaders(path, method, initial),
    });
  const response = await send();
  if (cookieMutation && response.status === 403) {
    const data = await response
      .clone()
      .json()
      .catch(() => null);
    if (data?.error?.code === "csrf_failed") {
      await refreshCsrfCookie();
      return send();
    }
  }
  return response;
}

export async function request<T = JsonObject>(
  path: string,
  options: RequestInit = {},
  authentication: "stored-key" | "session" = "stored-key",
): Promise<T> {
  const headers = csrfHeaders(path, options.method || "GET", options.headers);
  headers.set("Accept", "application/json");
  if (options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (authentication === "session") headers.delete("X-API-Key");
  const apiKey =
    authentication === "stored-key" ? sessionStorage.getItem("apiKey") : null;
  if (
    apiKey &&
    !headers.has("X-API-Key") &&
    new URL(path, window.location.href).origin === window.location.origin
  )
    headers.set("X-API-Key", apiKey);
  const response = await csrfFetch(path, {
    credentials: "same-origin",
    ...options,
    headers,
  });
  const text = await response.text();
  let data: JsonObject;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { detail: text };
  }
  if (!response.ok) {
    const error = data.error;
    const message =
      typeof error === "object" && error !== null
        ? String(
            (error as JsonObject).message ||
              (error as JsonObject).code ||
              response.statusText,
          )
        : String(error || data.detail || `HTTP ${response.status}`);
    const record =
      typeof error === "object" && error !== null ? (error as JsonObject) : {};
    throw new ApiError(
      response.status,
      String(record.code || ""),
      message,
      record.details,
      String(
        (record.details as JsonObject | null)?.request_id ||
          response.headers.get("X-Request-ID") ||
          "",
      ),
    );
  }
  return data as T;
}

const body = (value: JsonObject): BodyInit => JSON.stringify(value);
const mutation = (method: string, value?: JsonObject): RequestInit => ({
  method,
  ...(value ? { body: body(value) } : {}),
});
export const api = {
  requestCapacity: () =>
    request<RequestCapacity>("/api/settings/request-capacity", {}, "session"),
  updateRequestCapacity: (value: {
    long_request_limit: number;
    max_queued_requests: number;
  }) =>
    request<RequestCapacity>(
      "/api/settings/request-capacity",
      mutation("PATCH", value),
      "session",
    ),
  exportRunHistory: async (
    kind: "codex" | "tasks",
    query: Record<string, string | number | undefined>,
  ): Promise<Blob> => {
    const path =
      kind === "codex"
        ? "/api/codex/runs/export.xlsx"
        : "/api/runs/export.xlsx";
    const response = await csrfFetch(`${path}?${queryString(query)}`, {
      credentials: "same-origin",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(
        String(
          data.error?.message ||
            data.error?.code ||
            data.detail ||
            `HTTP ${response.status}`,
        ),
      );
    }
    if (
      !response.headers
        .get("Content-Type")
        ?.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        )
    ) {
      throw new Error(`Unexpected export response (HTTP ${response.status})`);
    }
    return response.blob();
  },
  stopKnowledgeUpdate: (requestId: string) =>
    request<KnowledgeUpdateStop>(
      `/api/knowledge/update-runs/${encodeURIComponent(requestId)}/stop`,
      mutation("POST", {}),
      "session",
    ),
  startKnowledgeUpdate: (value: KnowledgeUpdateRequest) =>
    request<KnowledgeUpdateAccepted>(
      "/api/knowledge/update-runs",
      mutation("POST", value),
      "session",
    ),
  sourceRepos: (page = 1, pageSize = 10) =>
    request<SourceRepoStatus>(
      `/api/source-repos?page=${page}&page_size=${pageSize}`,
      {},
      "session",
    ),
  refreshSources: (repositories?: string[]) =>
    request(
      "/api/source-repos",
      mutation("POST", repositories ? { repositories } : {}),
      "session",
    ),
  saveSourceRepo: (value: JsonObject, name?: string) =>
    request(
      name
        ? `/api/source-repos/catalog/${encodeURIComponent(name)}`
        : "/api/source-repos/catalog",
      mutation(name ? "PATCH" : "POST", value),
      "session",
    ),
  deleteSourceRepo: (name: string) =>
    request(
      `/api/source-repos/catalog/${encodeURIComponent(name)}`,
      mutation("DELETE"),
      "session",
    ),
  stopSourceRun: (id: string) =>
    request(
      `/api/source-repos/history/${encodeURIComponent(id)}/stop`,
      mutation("POST"),
      "session",
    ),
  setup: () =>
    request<{ required: boolean; code_file?: string }>("/api/auth/setup"),
  login: (value: JsonObject) =>
    request<{ token: string; user: UserRecord }>(
      "/api/auth/login",
      mutation("POST", value),
    ),
  register: (value: JsonObject) =>
    request<{ user: UserRecord; pending: boolean }>(
      "/api/auth/register",
      mutation("POST", value),
    ),
  logout: () => request("/api/auth/logout", mutation("POST")),
  me: () => request<{ user: UserRecord }>("/api/auth/me"),
  updateProfile: (value: JsonObject) =>
    request<{ user: UserRecord }>("/api/auth/me", mutation("PATCH", value)),
  changePassword: (value: JsonObject) =>
    request("/api/auth/password", mutation("POST", value)),
  users: () => request<{ items: UserRecord[] }>("/api/users"),
  createUser: (value: JsonObject) =>
    request<{ user: UserRecord }>("/api/users", mutation("POST", value)),
  updateUser: (id: number, value: JsonObject) =>
    request<{ user: UserRecord }>(`/api/users/${id}`, mutation("PATCH", value)),
  deleteUser: (id: number) => request(`/api/users/${id}`, mutation("DELETE")),
  keys: () => request<{ items: KeyMetadata[] }>("/api/keys"),
  createKey: (name: string, scopes: string[]) =>
    request<{ key: string; metadata: KeyMetadata }>(
      "/api/keys",
      mutation("POST", { name, scopes }),
    ),
  deleteKeyRecord: (id: string) =>
    request(
      `/api/keys/${encodeURIComponent(id)}/record`,
      mutation("DELETE"),
      "session",
    ),
  deleteKey: (id: string) => request(`/api/keys/${id}`, mutation("DELETE")),
  knowledgeCatalog: () =>
    request<{ catalog: KnowledgeCatalog }>("/api/catalog"),
  knowledgeList: (
    resource: string,
    query: Record<string, string | number | undefined>,
  ) => request<KnowledgePage>(`/api/${resource}?${queryString(query)}`),
  documents: (query: Record<string, string | number | undefined>) =>
    request<KnowledgePage<MarkdownDocument>>(
      `/api/documents?${queryString(query)}`,
    ),
  document: (id: string) =>
    request<{ document: MarkdownDocument }>(
      `/api/documents/${encodeURIComponent(id)}`,
    ),
  documentContent: (id: string) =>
    request<{ document: MarkdownDocument }>(
      `/api/documents/${encodeURIComponent(id)}/content`,
    ),
  uploadKnowledgeFile: (form: FormData) =>
    request<{ file: JsonObject }>("/api/files", { method: "POST", body: form }),
  knowledgeSearch: (query: Record<string, string | number | undefined>) =>
    request<KnowledgePage & JsonObject>(`/api/search?${queryString(query)}`),
  knowledgeSuggestions: (query: Record<string, string | number | undefined>) =>
    request<{ items: JsonObject[]; count: number }>(
      `/api/search/suggestions?${queryString(query)}`,
    ),
  knowledgeDetail: (resource: string, id: string, repository = "") =>
    request<JsonObject>(
      `/api/${resource}/${encodeURIComponent(id)}${
        repository ? `?repository=${encodeURIComponent(repository)}` : ""
      }`,
    ),
  createKnowledgeResource: (resource: string, value: JsonObject) =>
    request<JsonObject>(`/api/${resource}`, mutation("POST", value)),
  updateKnowledgeResource: (
    resource: string,
    id: string,
    value: JsonObject,
    repository = "",
  ) =>
    request<JsonObject>(
      `/api/${resource}/${encodeURIComponent(id)}${
        repository ? `?repository=${encodeURIComponent(repository)}` : ""
      }`,
      mutation("PATCH", value),
    ),
  deleteKnowledgeResource: (resource: string, id: string, repository = "") =>
    request<JsonObject>(
      `/api/${resource}/${encodeURIComponent(id)}${
        repository ? `?repository=${encodeURIComponent(repository)}` : ""
      }`,
      mutation("DELETE"),
    ),
  createContractRelation: (
    contractId: string,
    relation: "behaviors" | "consumers",
    value: JsonObject,
  ) =>
    request<JsonObject>(
      `/api/contracts/${encodeURIComponent(contractId)}/${relation}`,
      mutation("POST", value),
    ),
  deleteContractRelation: (
    contractId: string,
    relation: "behaviors" | "consumers",
    id: string,
  ) =>
    request<JsonObject>(
      `/api/contracts/${encodeURIComponent(contractId)}/${relation}/${encodeURIComponent(id)}`,
      mutation("DELETE"),
    ),
  rag: (q: string, topK: string) =>
    request<{ mode: string; backend: string; items: JsonObject[] }>(
      `/api/rag/search?q=${encodeURIComponent(q)}&top_k=${topK}`,
    ),
  tasks: () => request<{ items: TaskRecord[] }>("/api/tasks"),
  createTask: (value: JsonObject) =>
    request<{ task: TaskRecord }>("/api/tasks", mutation("POST", value)),
  updateTask: (id: string, value: JsonObject) =>
    request<{ task: TaskRecord }>(`/api/tasks/${id}`, mutation("PATCH", value)),
  deleteTask: (id: string) => request(`/api/tasks/${id}`, mutation("DELETE")),
  runTask: (id: string) => request(`/api/tasks/${id}/run`, mutation("POST")),
  runs: (query: Record<string, string | number | undefined> = {}) =>
    request<{
      items: JsonObject[];
      page: number;
      total_pages: number;
      total_count: number;
    }>(`/api/runs?${queryString(query)}`),
  taskRuns: (
    id: string,
    query: Record<string, string | number | undefined> = {},
  ) =>
    request<{ items: JsonObject[] }>(
      `/api/tasks/${id}/runs?${queryString({ limit: 100, ...query })}`,
    ),
  deleteTaskRuns: (id: string) =>
    request(`/api/tasks/${encodeURIComponent(id)}/runs`, mutation("DELETE")),
  run: (id: string) => request<{ run: JsonObject }>(`/api/runs/${id}`),
  deleteRun: (id: string) =>
    request(`/api/runs/${encodeURIComponent(id)}`, mutation("DELETE")),
  clearLogs: () => request("/api/logs", mutation("DELETE")),
  clearAuditEvents: () => request("/api/audit-events", mutation("DELETE")),
  auditEvents: (query: Record<string, string | number>) => {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== "") params.set(key, String(value));
    });
    return request<{
      items: AuditEvent[];
      count: number;
      total_count: number;
      page: number;
      page_size: number;
      total_pages: number;
    }>(`/api/audit-events?${params}`);
  },
  auditEvent: (id: number) =>
    request<{ event: AuditEvent }>(`/api/audit-events/${id}`),
  knowledgeReferences: (query: Record<string, string | number | undefined>) =>
    request<{ items: JsonObject[]; count: number }>(
      `/api/knowledge/references?${queryString(query)}`,
    ),
  docs: () =>
    request<{
      routes: JsonObject[];
      conventions: JsonObject;
      examples: JsonObject;
    }>("/api/docs"),
  codexModels: () => request<CodexModelCatalog>("/api/models", {}, "session"),
  codex: (value: CodexRequest) =>
    request<CodexResponse>(
      "/api/codex/execute",
      mutation("POST", value),
      "session",
    ),
  codexRunEvents: (id: string, page = 1, pageSize = 100) =>
    request<CodexRunEvents>(
      `/api/codex/runs/${encodeURIComponent(id)}/events?${queryString({ page, page_size: pageSize })}`,
      {},
      "session",
    ),
  codexRuns: (query: Record<string, string | number | undefined>) =>
    request<CodexRunPage>(
      `/api/codex/runs?${queryString(query)}`,
      {},
      "session",
    ),
  codexStatistics: () =>
    request<CodexStatistics>("/api/codex/statistics", {}, "session"),
  codexRun: (id: string) =>
    request<{ run: CodexRun }>(
      `/api/codex/runs/${encodeURIComponent(id)}`,
      {},
      "session",
    ),
  deleteCodexRun: (id?: string) =>
    request(
      `/api/codex/runs${id ? `/${encodeURIComponent(id)}` : ""}`,
      mutation("DELETE"),
      "session",
    ),
  skills: () => request<{ items: SkillRecord[]; count: number }>("/api/skills"),
  skill: (name: string) =>
    request<{ skill: SkillDetail }>(`/api/skills/${encodeURIComponent(name)}`),
  skillFile: (name: string, path: string) =>
    request<{ file: SkillTextFile }>(
      `/api/skills/${encodeURIComponent(name)}/files/${path
        .split("/")
        .map(encodeURIComponent)
        .join("/")}`,
    ),
  updateSkillFile: (
    name: string,
    path: string,
    content: string,
    expectedSha256: string,
  ) =>
    request<{ file: SkillTextFile }>(
      `/api/skills/${encodeURIComponent(name)}/files/${path
        .split("/")
        .map(encodeURIComponent)
        .join("/")}`,
      mutation("PUT", { content, expected_sha256: expectedSha256 }),
    ),
  updateSkill: (name: string, enabled: boolean) =>
    request<{ skill: SkillRecord }>(
      `/api/skills/${encodeURIComponent(name)}`,
      mutation("PATCH", { enabled }),
    ),
  deleteSkill: (name: string) =>
    request(`/api/skills/${encodeURIComponent(name)}`, mutation("DELETE")),
  importSkill: (file: File, name?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (name) form.append("name", name);
    return request<{ skill: SkillRecord }>("/api/skills", {
      method: "POST",
      body: form,
    });
  },
};
